import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../../../../../contexts/ProjectContext';
import {
  FiCheckCircle as CheckCircle, FiXCircle as XCircle, FiClock as Clock, FiFilter as Filter,
  FiPlay as Play, FiRefreshCw as RefreshCw, FiTrash2 as Trash2,
  FiZap as Zap, FiAlertCircle as AlertCircle, FiChevronDown as ChevronDown, FiChevronUp as ChevronUp,
  FiArrowRight as ArrowRight, FiTarget as Target, FiInfo as Info, FiHash as Hash,
  FiShield as Shield, FiList as List, FiCalendar as Calendar,
  FiSearch as Search,
  FiChevronLeft as ChevronLeft, FiChevronRight as ChevronRight,
} from 'react-icons/fi';
import {
  getEvaluationRuns,
  getEvaluationResults,
  deleteEvaluationRun,
  getSafetyResults,
  assignEvaluationRunProject,
  bulkAssignEvaluationRunProject,
  bulkAssignEvaluationRunWorkload,
} from '../../../../../services/aiOperationsApi';
import { SafetyResultsPanel, BedrockEvaluationProgress } from '../../../components/evaluations';
import ProjectAssignmentSelect from '../../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../../components/WorkloadAssignmentSelect';
import { showThrottlingToast, showThrottlingFailedToast, dismissThrottlingToast } from '../../../../../utils/apiToasts';
import type { EvaluationRun } from './RunDisplay';
import {
  RunDetailsCard,
  ErrorDetailsCard,
  QualityGateDetails,
  BedrockMetricsDisplay,
  BedrockResultsSummary,
} from './RunDisplay';

const RunsContent = () => {
  const { projects, hasProjectAccess } = useProject();
  const [runs, setRuns] = useState<EvaluationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [runResults, setRunResults] = useState<Record<string, Array<{ id?: string; test_case_name?: string; passed?: boolean; score?: number; duration_ms?: number; tokens_used?: number; metrics?: { criteria_scores?: Array<{ name: string; score: number }>; error?: string }; expected_output?: string; actual_output?: string; input?: string; error_message?: string }>>>({});
  const [resultsLoading, setResultsLoading] = useState<Record<string, boolean>>({});
  const [safetyResults, setSafetyResults] = useState<Record<string, Array<{ test_type: string; passed: boolean; id?: string; score?: number; details?: string; issues?: Array<{ type: string; severity: string; description: string }> }>>>({});
  const [safetyLoading, setSafetyLoading] = useState<Record<string, boolean>>({});
  const [activeInnerTab, setActiveInnerTab] = useState<Record<string, string>>({}); // Track active inner tab per run
  const [expandedResults, setExpandedResults] = useState<Record<string, boolean>>({}); // Track expanded test case rows

  // Filter state
  const [timeFilter, setTimeFilter] = useState('7d'); // 1d, 7d, 30d, 90d, all
  const [statusFilter, setStatusFilter] = useState(''); // '', completed, failed, partial, running
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRunIds, setSelectedRunIds] = useState<string[]>([]);
  const pageSize = 20;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedRunIds([]);
  }, [timeFilter, statusFilter, debouncedSearch]);

  useEffect(() => {
    loadRuns();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, statusFilter, debouncedSearch, currentPage]);

  // Real-time polling: Refresh while any run is pending/running/throttled
  useEffect(() => {
    const hasActiveRuns = runs.some(r => ['running', 'pending', 'throttled'].includes(r.status));
    if (!hasActiveRuns) return;

    const pollInterval = setInterval(() => {
      loadRuns();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runs]);

  // Detect throttled evaluations and show appropriate toast notifications
  useEffect(() => {
    // Find any throttled run and show the toast
    const throttledRun = runs.find(r => r.status === 'throttled');
    if (throttledRun?.retry_at) {
      showThrottlingToast(throttledRun.retry_at, throttledRun.retry_count || 1);
    }

    // Find any run that failed due to throttling (after max retries)
    const failedThrottledRun = runs.find(r =>
      r.status === 'failed' && r.throttle_reason === 'aws_bedrock_rate_limit'
    );
    if (failedThrottledRun) {
      showThrottlingFailedToast();
    }

    // Dismiss throttling toast when a previously throttled run completes
    const completedRuns = runs.filter(r => r.status === 'completed');
    if (completedRuns.length > 0 && !throttledRun) {
      dismissThrottlingToast();
    }
  }, [runs]);

  // Poll results for expanded runs that are still active
  useEffect(() => {
    if (!expandedRun) return;

    const run = runs.find(r => r.id === expandedRun);
    if (!run || (run.status !== 'running' && run.status !== 'pending')) return;

    const pollInterval = setInterval(() => {
      // Clear cached results to force reload
      setRunResults((prev) => {
        const newResults = { ...prev };
        if (expandedRun) delete newResults[expandedRun];
        return newResults;
      });
      loadRunResults(expandedRun);
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRun, runs]);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      };

      // Add time filter
      if (timeFilter && timeFilter !== 'all') {
        const daysMap: Record<string, number> = { '1d': 1, '7d': 7, '30d': 30, '90d': 90 };
        params.days = daysMap[timeFilter] || 7;
      }

      // Add status filter
      if (statusFilter) {
        params.status = statusFilter;
      }

      // Add search query
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      const data = await getEvaluationRuns(params);
      setRuns(data.data || []);
      setTotalCount(data.total || 0);
      setSelectedRunIds((prev) => prev.filter((id) => (data.data || []).some((run: EvaluationRun) => run.id === id)));
    } catch (err: unknown) {
      console.error('Error loading evaluation runs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRunResults = useCallback(async (runId: string, forceRefresh = false) => {
    // Skip if already loaded (unless force refresh)
    // Note: Use hasOwnProperty to distinguish "not loaded" from "loaded as empty array"
    if (!forceRefresh && Object.prototype.hasOwnProperty.call(runResults, runId)) return;

    setResultsLoading((prev) => ({ ...prev, [runId]: true }));
    try {
      const results = await getEvaluationResults(runId);
      setRunResults((prev) => ({ ...prev, [runId]: results }));
    } catch (err: unknown) {
      console.error('Error loading run results:', err);
    } finally {
      setResultsLoading((prev) => ({ ...prev, [runId]: false }));
    }
  }, [runResults]);

  const loadSafetyResults = useCallback(async (runId: string) => {
    if (safetyResults[runId]) return;

    setSafetyLoading((prev) => ({ ...prev, [runId]: true }));
    try {
      const response = await getSafetyResults({ evaluationRunId: runId });
      // API returns { items: [...], total, limit, offset }
      setSafetyResults((prev) => ({ ...prev, [runId]: response.items || [] }));
    } catch (err: unknown) {
      console.error('Error loading safety results:', err);
      // Set empty array on error so we don't keep retrying
      setSafetyResults((prev) => ({ ...prev, [runId]: [] }));
    } finally {
      setSafetyLoading((prev) => ({ ...prev, [runId]: false }));
    }
  }, [safetyResults]);

  const handleExpandRun = (runId: string, run: Record<string, any>) => {
    if (expandedRun === runId) {
      setExpandedRun(null);
    } else {
      setExpandedRun(runId);
      loadRunResults(runId);
      // Load safety results if safety testing was enabled for this run
      if (run.include_safety) {
        loadSafetyResults(runId);
      }
      // Set default inner tab
      if (!activeInnerTab[runId]) {
        setActiveInnerTab((prev) => ({ ...prev, [runId]: 'results' }));
      }
    }
  };

  const handleDeleteRun = async (runId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this evaluation run?')) return;
    try {
      await deleteEvaluationRun(runId);
      setRuns(prev => prev.filter(r => r.id !== runId));
    } catch (err: unknown) {
      console.error('Error deleting run:', err);
    }
  };

  const toggleRunSelection = (runId: string) => {
    setSelectedRunIds((prev) => (
      prev.includes(runId)
        ? prev.filter((id) => id !== runId)
        : [...prev, runId]
    ));
  };

  const toggleSelectAllRuns = () => {
    if (selectedRunIds.length === runs.length) {
      setSelectedRunIds([]);
      return;
    }
    setSelectedRunIds(runs.map((run) => run.id));
  };

  const handleAssignRunProject = async (runId: string, projectId: string | null) => {
    await assignEvaluationRunProject(runId, projectId);
    setRuns((prev) => prev.map((run) => (
      run.id === runId ? { ...run, project_id: projectId } : run
    )));
  };

  const handleBulkAssignProject = async (projectId: string | null) => {
    await bulkAssignEvaluationRunProject(selectedRunIds, projectId);
    setRuns((prev) => prev.map((run) => (
      selectedRunIds.includes(run.id) ? { ...run, project_id: projectId } : run
    )));
    setSelectedRunIds([]);
  };

  const handleBulkAssignWorkload = async (workloadId: string | null) => {
    await bulkAssignEvaluationRunWorkload(selectedRunIds, workloadId);
    setSelectedRunIds([]);
    loadRuns();
  };

  const getProjectName = (projectId?: string | null) => (
    projectId ? (projects.find((project) => project.id === projectId)?.name ?? 'Project') : null
  );

  const formatNumber = (num: number | null | undefined) => (!num && num !== 0) ? '0' : num.toLocaleString();
  const formatPercentage = (num: number | null | undefined) => (!num && num !== 0) ? '0%' : `${((num ?? 0) * 100).toFixed(1)}%`;
  const formatDuration = (ms: number | null | undefined) => {
    if (!ms && ms !== 0) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };
  const formatTimestamp = (timestamp: string | null | undefined) => timestamp ? new Date(timestamp).toLocaleString() : 'N/A';
  const formatCost = (cost: number | null | undefined) => (!cost && cost !== 0) ? '$0.00' : `$${(cost ?? 0).toFixed(4)}`;

  const getScoreColor = (score: number | null | undefined) => {
    if (!score && score !== 0) return 'text-gray-600';
    const normalizedScore = score > 1 ? score : score * 100;
    if (normalizedScore >= 80) return 'text-green-600';
    if (normalizedScore >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status: string | null | undefined) => {
    const statusConfig = {
      completed: {
        bg: 'bg-green-100 dark:bg-green-900/50',
        text: 'text-green-800 dark:text-green-300',
        icon: CheckCircle,
        tooltip: 'Evaluation finished successfully with all test cases processed'
      },
      running: {
        bg: 'bg-blue-100 dark:bg-blue-900/50',
        text: 'text-blue-800 dark:text-blue-300',
        icon: RefreshCw,
        tooltip: 'Evaluation is currently running - results will appear when complete'
      },
      pending: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/50',
        text: 'text-yellow-800 dark:text-yellow-300',
        icon: Clock,
        tooltip: 'Evaluation is queued and waiting to start'
      },
      partial: {
        bg: 'bg-orange-100 dark:bg-orange-900/50',
        text: 'text-orange-800 dark:text-orange-300',
        icon: AlertCircle,
        tooltip: 'Evaluation completed with some test cases skipped or errored'
      },
      failed: {
        bg: 'bg-red-100 dark:bg-red-900/50',
        text: 'text-red-800 dark:text-red-300',
        icon: XCircle,
        tooltip: 'Evaluation failed due to an error - check logs for details'
      },
      throttled: {
        bg: 'bg-amber-100 dark:bg-amber-900/50',
        text: 'text-amber-800 dark:text-amber-300',
        icon: RefreshCw,
        tooltip: 'Evaluation paused due to API rate limits - will resume automatically'
      },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
        title={config.tooltip}
      >
        <Icon className={`w-3.5 h-3.5 ${status === 'running' || status === 'throttled' ? 'animate-spin' : ''}`} />
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  if (loading && runs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasFilters = timeFilter !== 'all' || statusFilter || debouncedSearch;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm transition-colors overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Evaluation Runs</h2>
        <button
          onClick={loadRuns}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters Bar */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex flex-wrap items-center gap-3">
          {/* Time Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="1d">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="failed">Failed</option>
              <option value="throttled">Throttled</option>
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search by prompt or dataset name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
            {totalCount} {totalCount === 1 ? 'run' : 'runs'}
            {hasFilters && ' (filtered)'}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={runs.length > 0 && selectedRunIds.length === runs.length}
              onChange={toggleSelectAllRuns}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Select page
          </label>
        </div>
      </div>

      {hasProjectAccess && selectedRunIds.length > 0 && (
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-blue-900/20 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {selectedRunIds.length} selected
          </span>
          <ProjectAssignmentSelect
            bulk
            size="sm"
            onAssign={handleBulkAssignProject}
          />
          <WorkloadAssignmentSelect
            bulk
            size="sm"
            onAssign={handleBulkAssignWorkload}
          />
          <button
            onClick={() => setSelectedRunIds([])}
            className="text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
          >
            Clear
          </button>
        </div>
      )}

      {runs.length === 0 && !loading ? (
        <div className="p-12">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Play className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Run Your First Evaluation
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Test your prompts against datasets to measure quality, detect regressions, and ensure production readiness.
            </p>

            {/* Prerequisites */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-6 text-left">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Before you can run evaluations:</h4>
              <div className="space-y-2">
                <Link
                  to="/ai-operations/registry?tab=prompts"
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>Create at least one <span className="font-medium">prompt</span></span>
                  <ArrowRight className="w-3 h-3 ml-auto" />
                </Link>
                <Link
                  to="/ai-operations/registry?tab=datasets"
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                >
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span>Create a <span className="font-medium">dataset</span> with test cases</span>
                  <ArrowRight className="w-3 h-3 ml-auto" />
                </Link>
              </div>
            </div>

            {/* How to run */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-left">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">How to start an evaluation:</h4>
              <ol className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                <li>1. Go to <Link to="/ai-operations/registry?tab=prompts" className="underline hover:no-underline">Registry → Prompts</Link></li>
                <li>2. Click on a prompt to view its details</li>
                <li>3. Click "Run Evaluation" and select a dataset</li>
              </ol>
            </div>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {runs.map((run) => (
            <div key={run.id}>
              <div
                className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                onClick={() => handleExpandRun(run.id, run)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleExpandRun(run.id, run); }} role="button" tabIndex={0}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedRunIds.includes(run.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleRunSelection(run.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {expandedRun === run.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900 dark:text-white">{run.model_id || 'Unknown Model'}</span>
                        {getStatusBadge(run.status)}
                        {run.quality_gate_passed != null && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              run.quality_gate_passed
                                ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                                : 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                            }`}
                            title={run.quality_gate_passed
                              ? 'Quality gate passed - all configured thresholds (pass rate, score, latency, cost) were met'
                              : 'Quality gate failed - one or more thresholds were not met. Expand to see details'
                            }
                          >
                            <Zap className="w-3 h-3" />
                            Gate: {run.quality_gate_passed ? 'Passed' : 'Failed'}
                          </span>
                        )}
                        {run.include_safety && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                              run.safety_passed === false
                                ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                                : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                            }`}
                            title={run.safety_passed === false
                              ? 'Safety checks failed - harmful content detected in responses'
                              : 'Safety checks passed - no violations detected'
                            }
                          >
                            <Shield className="w-3 h-3" />
                            Safety
                          </span>
                        )}
                        {run.project_id && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                            {getProjectName(run.project_id)}
                          </span>
                        )}
                        {(run as any).workload_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300">
                            {(run as any).workload_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{formatTimestamp(run.created_at)}</p>
                    </div>
                  </div>

                  {/* Bedrock Evaluation Progress - shown for runs with bedrock_job_arn that are running/pending */}
                  {run.bedrock_job_arn && (['running', 'pending'].includes(run.status)) && (
                    <div className="ml-12 mt-2">
                      <BedrockEvaluationProgress
                        status={run.status}
                        bedrockJobStatus={run.bedrock_job_status ?? undefined}
                        bedrockJobArn={run.bedrock_job_arn ?? undefined}
                        startedAt={run.started_at || run.created_at}
                        lastPolledAt={run.bedrock_last_polled_at ?? undefined}
                        errorMessage={run.error_message ?? undefined}
                        testCaseCount={run.total_test_cases || 10}
                        timeoutMinutes={run.bedrock_timeout_minutes || 60}
                        compact={true}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-6">
                    {hasProjectAccess && (
                      <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
                        <ProjectAssignmentSelect
                          currentProjectId={run.project_id}
                          onAssign={(projectId) => handleAssignRunProject(run.id, projectId)}
                          size="sm"
                        />
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Test Cases</p>
                      <p className="font-medium text-gray-900 dark:text-white">{run.passed_count || 0}/{run.total_test_cases || 0}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Pass Rate</p>
                      <p className={`font-medium ${getScoreColor(run.pass_rate)}`}>{formatPercentage(run.pass_rate || 0)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Cost</p>
                      <p className="font-medium text-gray-900 dark:text-white">{formatCost(run.total_cost || 0)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteRun(run.id); }}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete run"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {expandedRun === run.id && (
                <div className="px-6 pb-4 bg-gray-50 dark:bg-gray-900/50">
                  {/* Run Details Card - Configuration and Metrics */}
                  <RunDetailsCard run={run} />

                  {/* Error Details - for failed runs */}
                  <ErrorDetailsCard run={run} />

                  {/* Quality Gate Details - shows why it passed/failed */}
                  <QualityGateDetails run={run} />

                  {/* Bedrock Metrics Display - for completed bedrock evaluations */}
                  {run.status === 'completed' && <BedrockMetricsDisplay run={run} />}

                  {/* Bedrock Evaluation Progress - detailed view in expanded section */}
                  {run.bedrock_job_arn && (['running', 'pending'].includes(run.status)) && (
                    <div className="mb-4">
                      <BedrockEvaluationProgress
                        status={run.status}
                        bedrockJobStatus={run.bedrock_job_status ?? undefined}
                        bedrockJobArn={run.bedrock_job_arn ?? undefined}
                        startedAt={run.started_at || run.created_at}
                        lastPolledAt={run.bedrock_last_polled_at ?? undefined}
                        errorMessage={run.error_message ?? undefined}
                        testCaseCount={run.total_test_cases || 10}
                        timeoutMinutes={run.bedrock_timeout_minutes || 60}
                        compact={false}
                      />
                    </div>
                  )}

                  {/* Inner tabs for Results vs Safety */}
                  {run.include_safety && (
                    <div className="flex gap-2 mb-4 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveInnerTab((prev) => ({ ...prev, [run.id]: 'results' }));
                        }}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                          activeInnerTab[run.id] === 'safety'
                            ? 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                            : 'border-blue-500 text-blue-600 dark:text-blue-400'
                        }`}
                      >
                        <List className="w-4 h-4" />
                        Results
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveInnerTab((prev) => ({ ...prev, [run.id]: 'safety' }));
                        }}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                          activeInnerTab[run.id] === 'safety'
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
                        }`}
                      >
                        <Shield className="w-4 h-4" />
                        Safety
                        {(safetyResults[run.id]?.length ?? 0) > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 rounded-full">
                            {safetyResults[run.id].filter((r) => !r.passed).length}
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Results Tab Content */}
                  {activeInnerTab[run.id] !== 'safety' && (
                    <>
                      {resultsLoading[run.id] && (
                        <div className="py-8 text-center">
                          <RefreshCw className="w-6 h-6 text-gray-400 mx-auto mb-2 animate-spin" />
                          <p className="text-sm text-gray-500">Loading results...</p>
                        </div>
                      )}
                      {!resultsLoading[run.id] && (runResults[run.id]?.length ?? 0) > 0 && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Test Case</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Score</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Duration</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tokens</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {runResults[run.id].map((result, idx: number) => {
                                // Parse metrics if it's a string
                                const metrics = typeof result.metrics === 'string'
                                  ? JSON.parse(result.metrics)
                                  : result.metrics;
                                const hasError = metrics?.error || result.error_message;
                                const isExpanded = result.id ? expandedResults[result.id] : false;
                                let resultStatusIcon: React.ReactNode;
                                if (result.passed) resultStatusIcon = <CheckCircle className="w-5 h-5 text-green-600" />;
                                else if (hasError) resultStatusIcon = <AlertCircle className="w-5 h-5 text-yellow-600" />;
                                else resultStatusIcon = <XCircle className="w-5 h-5 text-red-600" />;

                                return (
                                  <React.Fragment key={result.id || `result-${idx}`}>
                                    <tr
                                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${hasError ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
                                      onClick={() => setExpandedResults((prev) => ({
                                        ...prev,
                                        [result.id || '']: !prev[result.id || '']
                                      }))} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedResults((prev) => ({
                                        ...prev,
                                        [result.id || '']: !prev[result.id || '']
                                      })); }} role="row" tabIndex={0}
                                    >
                                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                        <div className="flex items-center gap-2">
                                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                          {result.test_case_name || `Test Case ${idx + 1}`}
                                        </div>
                                      </td>
                                      <td className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                          {resultStatusIcon}
                                          {hasError && <span className="text-xs text-red-600 dark:text-red-400">Error</span>}
                                        </div>
                                      </td>
                                      <td className={`px-4 py-2 text-sm font-medium ${getScoreColor(result.score)}`}>{formatPercentage(result.score || 0)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{formatDuration(result.duration_ms)}</td>
                                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">{formatNumber(result.tokens_used || 0)}</td>
                                    </tr>
                                    {/* Expanded Details Row */}
                                    {isExpanded && (
                                      <tr>
                                        <td colSpan={5} className="px-4 py-4 bg-gray-50 dark:bg-gray-800/50">
                                          <div className="space-y-4">
                                            {/* Scoring Failed Warning */}
                                            {(metrics?.judge_failed || metrics?.scoring_failed) && (
                                              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                                <p className="text-sm text-yellow-800 dark:text-yellow-200 flex items-center gap-2">
                                                  <AlertCircle className="w-4 h-4" />
                                                  {metrics?.judge_failed ? 'LLM Judge scoring failed' : 'Semantic similarity scoring failed'} - score set to 0
                                                </p>
                                              </div>
                                            )}
                                            {/* Error Section */}
                                            {hasError && (
                                              <div className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
                                                <h5 className="font-medium text-red-800 dark:text-red-200 mb-1 flex items-center gap-2">
                                                  <AlertCircle className="w-4 h-4" />
                                                  Error Details
                                                </h5>
                                                <p className="text-sm text-red-700 dark:text-red-300 break-words">
                                                  {metrics?.error || result.error_message}
                                                </p>
                                              </div>
                                            )}
                                            {/* Judge Assessment */}
                                            {metrics?.judge_assessment && (
                                              <div>
                                                <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-1">Judge Assessment</h5>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-900 p-2 rounded">
                                                  {metrics.judge_assessment}
                                                </p>
                                              </div>
                                            )}
                                            {/* Criteria Scores */}
                                            {(metrics?.criteria_scores?.length ?? 0) > 0 && (
                                              <div>
                                                <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Criteria Scores</h5>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                  {metrics.criteria_scores!.map((cs: { name: string; score: number }) => (
                                                    <div key={cs.name} className="flex items-center justify-between bg-gray-100 dark:bg-gray-900 p-2 rounded text-sm">
                                                      <span className="text-gray-600 dark:text-gray-400">{cs.name || 'Criterion'}</span>
                                                      <span className={`font-medium ${getScoreColor(cs.score)}`}>{formatPercentage(cs.score || 0)}</span>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                            {/* BERTScore Metrics */}
                                            {(metrics?.bert_f1 !== undefined || metrics?.bert_score) && (
                                              <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                                <h5 className="font-medium text-purple-800 dark:text-purple-200 mb-2 flex items-center gap-2">
                                                  <Target className="w-4 h-4" />
                                                  BERTScore
                                                  {metrics?.bert_model && (
                                                    <span className="text-xs font-normal text-purple-600 dark:text-purple-400">({metrics.bert_model})</span>
                                                  )}
                                                </h5>
                                                <div className="grid grid-cols-3 gap-3">
                                                  <div className="text-center">
                                                    <p className="text-xs text-purple-600 dark:text-purple-400">Precision</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.bert_precision || metrics?.bert_score?.precision)}`}>
                                                      {formatPercentage(metrics?.bert_precision || metrics?.bert_score?.precision || 0)}
                                                    </p>
                                                  </div>
                                                  <div className="text-center">
                                                    <p className="text-xs text-purple-600 dark:text-purple-400">Recall</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.bert_recall || metrics?.bert_score?.recall)}`}>
                                                      {formatPercentage(metrics?.bert_recall || metrics?.bert_score?.recall || 0)}
                                                    </p>
                                                  </div>
                                                  <div className="text-center">
                                                    <p className="text-xs text-purple-600 dark:text-purple-400">F1</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.bert_f1 || metrics?.bert_score?.f1)}`}>
                                                      {formatPercentage(metrics?.bert_f1 || metrics?.bert_score?.f1 || 0)}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            {/* ROUGE Metrics */}
                                            {(metrics?.rouge_1_f1 !== undefined || metrics?.rouge_l_f1 !== undefined || metrics?.rouge) && (
                                              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                                <h5 className="font-medium text-green-800 dark:text-green-200 mb-2 flex items-center gap-2">
                                                  <Hash className="w-4 h-4" />
                                                  ROUGE Scores
                                                  {metrics?.rouge_primary_metric && (
                                                    <span className="text-xs font-normal text-green-600 dark:text-green-400">(Primary: {metrics.rouge_primary_metric})</span>
                                                  )}
                                                </h5>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                  <div className="text-center">
                                                    <p className="text-xs text-green-600 dark:text-green-400">ROUGE-1</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.rouge_1_f1 || metrics?.rouge?.rouge1_f1)}`}>
                                                      {formatPercentage(metrics?.rouge_1_f1 || metrics?.rouge?.rouge1_f1 || 0)}
                                                    </p>
                                                  </div>
                                                  <div className="text-center">
                                                    <p className="text-xs text-green-600 dark:text-green-400">ROUGE-2</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.rouge_2_f1 || metrics?.rouge?.rouge2_f1)}`}>
                                                      {formatPercentage(metrics?.rouge_2_f1 || metrics?.rouge?.rouge2_f1 || 0)}
                                                    </p>
                                                  </div>
                                                  <div className="text-center">
                                                    <p className="text-xs text-green-600 dark:text-green-400">ROUGE-L</p>
                                                    <p className={`font-bold ${getScoreColor(metrics?.rouge_l_f1 || metrics?.rouge?.rougeL_f1)}`}>
                                                      {formatPercentage(metrics?.rouge_l_f1 || metrics?.rouge?.rougeL_f1 || 0)}
                                                    </p>
                                                  </div>
                                                  {(metrics?.rouge_lsum_f1 !== undefined || metrics?.rouge?.rougeLsum_f1 !== undefined) && (
                                                    <div className="text-center">
                                                      <p className="text-xs text-green-600 dark:text-green-400">ROUGE-Lsum</p>
                                                      <p className={`font-bold ${getScoreColor(metrics?.rouge_lsum_f1 || metrics?.rouge?.rougeLsum_f1)}`}>
                                                        {formatPercentage(metrics?.rouge_lsum_f1 || metrics?.rouge?.rougeLsum_f1 || 0)}
                                                      </p>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                            {/* Secondary Metrics */}
                                            {(metrics?.secondary_bert_score || metrics?.secondary_rouge || metrics?.secondary_semantic_similarity) && (
                                              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                                <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                                                  <Info className="w-4 h-4" />
                                                  Secondary Metrics
                                                  <span className="text-xs font-normal text-gray-500">(informational only)</span>
                                                </h5>
                                                <div className="space-y-2">
                                                  {metrics?.secondary_semantic_similarity && (
                                                    <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-sm">
                                                      <span className="text-blue-700 dark:text-blue-300">Semantic Similarity</span>
                                                      <span className={`font-medium ${getScoreColor(metrics.secondary_semantic_similarity.score)}`}>
                                                        {formatPercentage(metrics.secondary_semantic_similarity.score || 0)}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {metrics?.secondary_bert_score && (
                                                    <div className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 p-2 rounded text-sm">
                                                      <span className="text-purple-700 dark:text-purple-300">BERTScore F1</span>
                                                      <span className={`font-medium ${getScoreColor(metrics.secondary_bert_score.f1)}`}>
                                                        {formatPercentage(metrics.secondary_bert_score.f1 || 0)}
                                                      </span>
                                                    </div>
                                                  )}
                                                  {metrics?.secondary_rouge && (
                                                    <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 p-2 rounded text-sm">
                                                      <span className="text-green-700 dark:text-green-300">ROUGE-L F1</span>
                                                      <span className={`font-medium ${getScoreColor(metrics.secondary_rouge.rougeL_f1)}`}>
                                                        {formatPercentage(metrics.secondary_rouge.rougeL_f1 || 0)}
                                                      </span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                            {/* Actual Output */}
                                            <div>
                                              <h5 className="font-medium text-gray-700 dark:text-gray-300 mb-1">Actual Output</h5>
                                              <pre className="text-xs text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto max-h-40 whitespace-pre-wrap">
                                                {result.actual_output || 'No output'}
                                              </pre>
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {!resultsLoading[run.id] && !((runResults[run.id]?.length ?? 0) > 0) && (
                        (() => {
                          // Check if this is a Bedrock evaluation with aggregate metrics
                          const modelParams = typeof run.model_parameters === 'string'
                            ? JSON.parse(run.model_parameters || '{}')
                            : (run.model_parameters || {});
                          const bedrockMetrics = modelParams.bedrock_metrics;
                          const isBedrock = run.bedrock_job_arn || modelParams.scoring_method === 'bedrock_evaluation';

                          if (isBedrock && bedrockMetrics && Object.keys(bedrockMetrics).length > 0) {
                            return <BedrockResultsSummary run={run} />;
                          }

                          // Provide context-aware message for empty results
                          const isRunning = ['running', 'pending'].includes(run.status);
                          const hasSummary = (run.passed_count ?? 0) > 0 || (run.failed_count ?? 0) > 0 || (run.avg_score ?? 0) > 0;

                          if (isRunning) {
                            return (
                              <div className="py-4 text-center">
                                <RefreshCw className="w-6 h-6 text-blue-400 mx-auto mb-2 animate-spin" />
                                <p className="text-sm text-gray-500">Evaluation is still running...</p>
                                <p className="text-xs text-gray-400 mt-1">Results will appear when complete</p>
                              </div>
                            );
                          }

                          if (hasSummary) {
                            // Evaluation has summary data but individual results may not be available
                            return (
                              <div className="py-4 text-center">
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-3 inline-block">
                                  <div className="grid grid-cols-3 gap-4 text-sm">
                                    <div>
                                      <p className="text-gray-500">Passed</p>
                                      <p className="font-bold text-green-600">{run.passed_count || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Failed</p>
                                      <p className="font-bold text-red-600">{run.failed_count || 0}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-500">Avg Score</p>
                                      <p className="font-bold text-blue-600">{((run.avg_score || 0) * 100).toFixed(1)}%</p>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-xs text-gray-400">Detailed results not available for this evaluation type</p>
                              </div>
                            );
                          }

                          // Default: no results and no summary
                          return (
                            <div className="py-4 text-center">
                              <p className="text-sm text-gray-500 mb-2">No results available for this run</p>
                              <button
                                onClick={() => loadRunResults(run.id, true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-200 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Retry loading results
                              </button>
                            </div>
                          );
                        })()
                      )}
                    </>
                  )}

                  {/* Safety Tab Content */}
                  {activeInnerTab[run.id] === 'safety' && (() => {
                    if (safetyLoading[run.id]) {
                      return (
                        <div className="py-8 text-center">
                          <RefreshCw className="w-6 h-6 text-gray-400 mx-auto mb-2 animate-spin" />
                          <p className="text-sm text-gray-500">Loading safety results...</p>
                        </div>
                      );
                    }
                    if ((safetyResults[run.id]?.length ?? 0) > 0) {
                      return <SafetyResultsPanel results={safetyResults[run.id]} />;
                    }
                    return (
                      <div className="py-4 text-center text-sm text-gray-500">
                        <Shield className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        No safety issues detected
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} runs
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RunsContent;
